import React, { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";
import Root from "@/layouts/Root";
import Layout from "@/components/organisms/Layout";
import PublicLayout from "@/components/organisms/PublicLayout";
import NotFound from "@/components/pages/NotFound";
import { getRouteConfig } from "@/router/route.utils";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import Callback from "@/pages/auth/Callback";
import ErrorPage from "@/pages/auth/ErrorPage";
import AccessDenied from "@/pages/auth/AccessDenied";
import ResetPassword from "@/pages/auth/ResetPassword";
import PromptPassword from "@/pages/auth/PromptPassword";
const Pubblico = lazy(() => import("@/components/pages/Pubblico"));
// Lazy load page components
const Dashboard = lazy(() => import("@/components/pages/Dashboard"));
const Spazi = lazy(() => import("@/components/pages/Spazi"));
const SpazioDettaglio = lazy(() => import("@/components/pages/SpazioDettaglio"));
const SpazioForm = lazy(() => import("@/components/organisms/SpazioForm"));
const Clienti = lazy(() => import("@/components/pages/Clienti"));
const ClienteDettaglio = lazy(() => import("@/components/pages/ClienteDettaglio"));
const ClienteForm = lazy(() => import("@/components/organisms/ClienteForm"));
const Prenotazioni = lazy(() => import("@/components/pages/Prenotazioni"));
const PrenotazioneForm = lazy(() => import("@/components/organisms/PrenotazioneForm"));
const Timeline = lazy(() => import("@/components/pages/Timeline"));
const Pagamenti = lazy(() => import("@/components/pages/Pagamenti"));
const PagamentoForm = lazy(() => import("@/components/organisms/PagamentoForm"));
const PubblicoDettaglio = lazy(() => import("@/components/pages/PubblicoDettaglio"));
const CompetenzeMensili = lazy(() => import("@/components/pages/CompetenzeMensili"));
const TestWebhook = lazy(() => import("@/components/pages/TestWebhook"));
const Preventivi = lazy(() => import("@/components/pages/Preventivi"));
const PreventivoForm = lazy(() => import("@/components/organisms/PreventivoForm"));
const PreventivoDettaglio = lazy(() => import("@/components/pages/PreventivoDettaglio"));

const Fallback = (
  <div className="min-h-screen flex items-center justify-center bg-background-primary">
    <div className="text-center space-y-4">
      <svg className="animate-spin h-12 w-12 text-primary mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      <p className="text-gray-400">Caricamento...</p>
    </div>
  </div>
);

const createRoute = ({ path, index, element, children, ...meta }) => {
  let configPath;
  if (index) {
    configPath = "/";
  } else {
    configPath = path && path.startsWith('/') ? path : `/${path || ''}`;
  }

  const config = getRouteConfig(configPath);

  const route = {
    ...(index ? { index: true } : { path }),
    element: element ? (
      <Suspense fallback={Fallback}>{element}</Suspense>
    ) : element,
    handle: {
      access: config?.allow,
      ...meta,
    },
  };

  if (children && children.length > 0) {
    route.children = children;
  }

  return route;
};

const routes = [
  {
    path: "/",
    element: <Root />,
    children: [
      // Auth routes
      { path: "login", element: <Login /> },
      { path: "signup", element: <Signup /> },
      { path: "callback", element: <Callback /> },
      { path: "error", element: <ErrorPage /> },
      { path: "access-denied", element: <AccessDenied /> },
      { path: "reset-password/:appId/:fields", element: <ResetPassword /> },
      { path: "prompt-password/:appId/:emailAddress/:provider", element: <PromptPassword /> },

      // Public layout routes
{
        path: "pubblico",
        element: <PublicLayout />,
        children: [
          {
            index: true,
            element: <Suspense fallback={Fallback}><Pubblico /></Suspense>
          },
          {
            path: ":id",
            element: <Suspense fallback={Fallback}><PubblicoDettaglio /></Suspense>
          },
          { path: "*", element: <NotFound /> }
        ]
      },

      // Main app layout routes
      {
        path: "/",
        element: <Layout />,
        children: [
          createRoute({ index: true, element: <Dashboard /> }),
          // Spazi
          createRoute({ path: "spazi", element: <Spazi /> }),
          createRoute({ path: "spazi/nuovo", element: <SpazioForm /> }),
          createRoute({ path: "spazi/modifica/:id", element: <SpazioForm isEdit={true} /> }),
          createRoute({ path: "spazi/:id", element: <SpazioDettaglio /> }),
          // Clienti
          createRoute({ path: "clienti", element: <Clienti /> }),
          createRoute({ path: "clienti/nuovo", element: <ClienteForm /> }),
          createRoute({ path: "clienti/modifica/:id", element: <ClienteForm isEdit={true} /> }),
          createRoute({ path: "clienti/:id", element: <ClienteDettaglio /> }),
          // Prenotazioni
createRoute({ path: "prenotazioni", element: <Prenotazioni /> }),
          createRoute({ path: "prenotazioni/nuova", element: <PrenotazioneForm /> }),
          createRoute({ path: "prenotazioni/modifica/:id", element: <PrenotazioneForm isEdit={true} /> }),
          // Timeline
          createRoute({ path: "timeline", element: <Timeline /> }),
// Pagamenti
          createRoute({ path: "pagamenti", element: <Pagamenti /> }),
          createRoute({ path: "pagamenti/nuovo", element: <PagamentoForm /> }),
          createRoute({ path: "pagamenti/modifica/:id", element: <PagamentoForm isEdit={true} /> }),
          // Competenze Mensili
createRoute({ path: "competenze-mensili", element: <CompetenzeMensili /> }),
// Preventivi
          createRoute({ path: "preventivi", element: <Preventivi /> }),
          createRoute({ path: "preventivi/nuovo", element: <PreventivoForm /> }),
          createRoute({ path: "preventivi/modifica/:id", element: <PreventivoForm isEdit={true} /> }),
          createRoute({ path: "preventivi/:id", element: <PreventivoDettaglio /> }),
          createRoute({ path: "test-webhook", element: <TestWebhook /> }),
        ],
      },
    ]
  }
];

export const router = createBrowserRouter(routes);